// Catalog.ID Client — `issue-mail-cert`: the mail_sign sub-key certification ceremony
// ============================================================================
// Custody surface for the two-level `mail_sign` model (mail migration §5.2;
// normative format: apps/mail/docs/impl/envelope-wire.md §6). The account-level
// ROOT lives in the CID keybag and NEVER leaves this tab; a per-machine mail
// SUB-KEY is presented here by the mail module and goes back certified.
//
// Custody boundary = the CID tab, inherited from chat M1's decision. A
// dedicated key-agent process stays a later option; OS keystore/TPM for
// sub-keys is deferred (migration §5.2, "decisions inherited").
//
// WHY THERE IS NO CONSENT MODAL, and what stands in its place
// -----------------------------------------------------------
// Copied verbatim from `chat-cert-handlers.ts`, because the argument transfers
// unchanged. The gate here is the keybag UNLOCK, not a per-request prompt:
//   1. The custody doctrine makes unlock itself the ceremony — "ceremony-grade
//      operations REQUIRE an explicit unlock by design".
//   2. Renewal piggybacks on any keybag unlock. A modal per issuance would fire
//      on every device every 60 days, which trains users to click through
//      exactly the prompt that matters.
//   3. Nothing here MUTATES the keybag, so the supersecret re-prompt does not
//      apply: this reads one entry and signs with it, the same shape as the EFS
//      `decapsulate-efs-encrypt` hop, which is likewise unlock-gated only.
// Contrast `mail-address-handlers.ts`, which DOES have a modal — it writes
// durable account state.
// What DOES gate the request: the hub's `_senderServiceId` stamp, the relay's
// module-identity signature check, and the digest binding below.
//
// Gate stack (in order):
//   1–3. sender stamp / requester / active identity — `dispatchMailRequest`.
//   4.   required fields present.
//   5.   `subkeyPubkey` is decodable base64.
//   6.   body↔canonical binding: SHA-512(raw sub-key) equals the signed
//        `subkeyPubkeyDigestBase64`. Closes the substituted-body gap.
//   7.   sub-key is EXACTLY 1984 bytes — "hybrid sub-keys, always" (§16).
//   8.   `validityDays` is a whole 1…90; default 60.
//   9.   wait-for-unlock (90 s when `requiresUserInteraction:true`, else fail
//        fast — nobody would see a prompt for a request that does not refocus
//        the tab).
//  10.   the `mail_sign` root is in the bag and is a 64-byte hybrid seed.
//  11.   issue via crypto/mail-certs (which re-checks 7, the 1…90 range, and
//        refuses root-certifies-itself), zeroizing the seed copy afterwards.
// ============================================================================

import { sha512 } from '@noble/hashes/sha2.js';
import { keys } from '../state/keys';
import { recordKeyUse } from '../state/key-use-record';
import * as aes from '../crypto/aes';
import {
    issueCert, MAIL_CERT_DEFAULT_VALIDITY_DAYS, MAIL_CERT_MAX_VALIDITY_DAYS,
} from '../crypto/mail-certs';
import { HYBRID_PUBLIC_KEY_LEN, HYBRID_SECRET_SEED_LEN } from '../crypto/signing';
import type { MailRequest, MailResponse, MailDispatchDeps } from './mail-types';
import { ensureUnlocked } from './unlock-gate';

/**
 * Keybag slot for the account-level mail signing root (migration §5.2).
 * Bag-only — the private half never gets an `accountpublickey` row, so the key
 * id is on `KeyBagEntryRepository`'s consistency skip-list (and its TS mirror
 * `BAG_ONLY_KEY_IDS`) or every write throws. The PUBLIC half publishes on the
 * `registry_signing_key` claim track under the `mail_root` class.
 */
export const MAIL_SIGN_KEY_ID = 'mail_sign';

type ResponseBase = Pick<MailResponse, 'type' | 'to' | 'requestId'>;

/**
 * Digest convention shared with the mail service's canonical builder and the
 * relay's C# mirror (`CrossModuleCanonical.ComputeSubkeyPubkeyDigest`): base64
 * SHA-512 over the RAW sub-key public key bytes, NOT over its base64 text.
 * Hashing the raw bytes keeps the commitment independent of base64
 * padding/encoding choices on either side.
 *
 * Deliberately the SAME recipe chat's issuance uses. The domain separation
 * lives in the MODE LITERAL (`issue-mail-cert` vs `issue-cert`) and in the cert
 * TYPE STRING, not in the digest — forking the digest would buy nothing and
 * would make the two C# mirrors diverge.
 */
export function computeSubkeyPubkeyDigest(subkeyPubkey: Uint8Array): string {
    return aes.toBase64(sha512(subkeyPubkey));
}

function unavailable(base: ResponseBase, reason: string): MailResponse {
    return { ...base, status: 'unavailable', reason };
}

export async function handleIssueMailCert(
    request: MailRequest,
    base: ResponseBase,
    accountCid: string,
    deps: MailDispatchDeps,
): Promise<MailResponse> {
    // Gate 4.
    if (!request.subkeyPubkey || !request.subkeyPubkeyDigestBase64) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5.
    let subkeyPubkey: Uint8Array;
    try {
        subkeyPubkey = aes.fromBase64(request.subkeyPubkey);
    } catch {
        return unavailable(base, 'bad-subkey-base64');
    }

    // Gate 6: body↔canonical binding.
    if (computeSubkeyPubkeyDigest(subkeyPubkey) !== request.subkeyPubkeyDigestBase64) {
        return unavailable(base, 'subkey-digest-mismatch');
    }

    // Gate 7: an Ed25519-only sub-key is refused as a FORMAT error, here and
    // again inside issueCert. Cheap, and it means a broken requester is told at
    // issuance rather than shipping a cert no verifier will honour.
    if (subkeyPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return unavailable(base, 'subkey-not-hybrid');
    }

    // Gate 8.
    const validityDays = request.validityDays ?? MAIL_CERT_DEFAULT_VALIDITY_DAYS;
    if (!Number.isInteger(validityDays)
        || validityDays < 1 || validityDays > MAIL_CERT_MAX_VALIDITY_DAYS) {
        return unavailable(base, 'validity-out-of-range');
    }

    // Gate 9: wait-for-unlock. `requiresUserInteraction:true` is what makes the
    // dashboard refocus this tab, so it is also what makes waiting meaningful.
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    // Gate 10, and ABSENT IS NOT LOCKED (device-setup-restructure §1 item 12,
    // §9.2). The mail root is an OPTIONAL key: a member may keep it off this
    // device (whitepaper §3.4.7). Certification is the interactive, tab-
    // refocusing request, so it is the one that may load it back — `requireKey`
    // prompts for the supersecret, decrypts this one key from the server bag
    // and offers to keep it here. Everything else answers `key-not-on-device`,
    // which is neither `cid-locked` nor "your root is gone".
    let rootSeedB64 = keys.getPrivateKey(MAIL_SIGN_KEY_ID);
    if (!rootSeedB64) {
        if (request.requiresUserInteraction === true && deps.requireKey) {
            try {
                await deps.requireKey(MAIL_SIGN_KEY_ID, 'certify this machine for mail');
            } catch {
                // A failed load is an absent key; the refusal below says so.
            }
            rootSeedB64 = keys.getPrivateKey(MAIL_SIGN_KEY_ID);
        }
        if (!rootSeedB64) return unavailable(base, 'key-not-on-device');
    }
    let rootSeed: Uint8Array;
    try {
        rootSeed = aes.fromBase64(rootSeedB64);
    } catch {
        return unavailable(base, 'mail-sign-root-malformed');
    }
    if (rootSeed.length !== HYBRID_SECRET_SEED_LEN) {
        rootSeed.fill(0);
        return unavailable(base, 'mail-sign-root-malformed');
    }

    // Gate 11. The seed copy is zeroed on every path — the keybag is a root of
    // certification, never an operational key. Zeroing this copy does not touch
    // the bag's own base64 string; the entry stays usable for the next renewal.
    try {
        const cert = await issueCert(rootSeed, subkeyPubkey, {
            // Bound to the ACTIVE identity. The requester does not get to name
            // the account it is being certified under.
            accountCid,
            validityDays,
        });
        // Evidence of use (§9.4): written only now that the cert exists, and as
        // the ORACLE it was — the root itself never left this tab.
        recordKeyUse(accountCid, MAIL_SIGN_KEY_ID, {
            requesterId: request.requesterId,
            mode: request.mode,
            kind: 'oracle',
        });
        return {
            ...base,
            status: 'approved',
            cert,
            subkeyFingerprint: toHexLower(sha512(subkeyPubkey)),
            rootFingerprint: cert.body.rootFingerprint,
        };
    } catch (err) {
        // issueCert's own refusals are stable codes (`subkey-is-root`,
        // `subkey-not-hybrid`, `validity-out-of-range`, `bad-account-cid`,
        // `bad-root-seed`); pass them through verbatim so mail can branch.
        const message = err instanceof Error ? err.message.slice(0, 80) : 'cert-issue-failed';
        return unavailable(base, message);
    } finally {
        rootSeed.fill(0);
    }
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}
